// ===== Download tab UI =====
// Pick title → 대분류(creative) → 소분류(file), list that file's canvases, then
// convert each (file base + canvas overrides) per ad network and download.

import { escapeHtml } from './util.js';
import { NETWORKS, convert, makeZip, utf8WithBom, outName } from './convert-core.js';
import { CONVERT_ICON, renderNetworkPicker, renderCard, wireResultCards, downloadAll } from './convert-ui.js';
import { buildDropdown } from './ui/dropdown.js';
import { titles } from './session.js';
import { applyOverrides } from './editor.js';
import { fetchFileSource } from './api.js';

const root = document.getElementById('downloadRoot');

let dl = { titleId: null, creativeId: null, fileId: null };
let selectedNetworks = new Set(NETWORKS.map(n => n.key));
let selectedCanvasIds = new Set();
let lastOkResults = [];   // successful conversions, used by the "모두 다운로드" button state

const DL_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`;
const TITLE_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>`;
const CREATIVE_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 3v18"/></svg>`;
const FILE_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`;

function curTitle() { return titles.find(t => t.id === dl.titleId) || null; }
function curCreative() { const t = curTitle(); return t ? (t.creatives.find(c => c.id === dl.creativeId) || null) : null; }
function curFile() { const c = curCreative(); return c ? ((c.files || []).find(f => f.id === dl.fileId) || null) : null; }

function buildForNetwork(source, canvas, networkKey) {
  const twMap = new Map((canvas.tw || []).map((e, i) => [i, e]));
  const patched = applyOverrides(source || '', canvas.pkg || {}, twMap);
  return convert(patched, networkKey);
}

// ===== Render =====
function render() {
  if (!root) return;
  root.innerHTML = `
    <div class="cv-head">
      <h2>다운로드</h2>
      <p>타이틀 · 대분류 · 소분류(파일)를 선택하면 그 파일의 캔버스를 광고 네트워크 규격으로 변환해 다운로드합니다.</p>
    </div>
    <div class="cv-source-body"><div class="cv-creative-pick" id="dlPick"></div></div>
    <div id="dlCanvasSec"></div>
    <div class="cv-section">
      <div class="cv-section-label">변환 대상 네트워크</div>
      <div class="cv-networks" id="dlNetworks"></div>
    </div>
    <div class="cv-actions">
      <button class="primary" id="dlBtn" data-mode="convert">${CONVERT_ICON}<span>변환</span></button>
      <button class="cv-ghost" id="dlReset" type="button">리셋</button>
      <span class="cv-hint" id="dlHint"></span>
    </div>
    <div class="cv-results" id="dlResults"></div>
  `;
  renderPick();
  renderCanvases();
  renderNetworkPicker(root.querySelector('#dlNetworks'), selectedNetworks, updateHint);
  renderResults([]);
  root.querySelector('#dlBtn').addEventListener('click', onMainBtn);
  root.querySelector('#dlReset').addEventListener('click', resetDownload);
  updateHint();
}

// Main action button is a two-state button: "변환" runs the conversion; once it
// succeeds, the same button becomes "모두 다운로드" and downloads every result.
// Any change to the selection reverts it to "변환" (via updateHint).
function setMainBtnMode(mode) {
  const btn = root.querySelector('#dlBtn');
  if (!btn) return;
  btn.dataset.mode = mode;
  btn.innerHTML = mode === 'download'
    ? `${DL_ICON}<span>모두 다운로드</span>`
    : `${CONVERT_ICON}<span>변환</span>`;
}
function onMainBtn() {
  const btn = root.querySelector('#dlBtn');
  if (btn && btn.dataset.mode === 'download') downloadAllResults();
  else runDownload();
}
function downloadAllResults() {
  return downloadAll(lastOkResults);
}

function resetDownload() {
  dl = { titleId: null, creativeId: null, fileId: null };
  selectedCanvasIds = new Set();
  selectedNetworks = new Set(NETWORKS.map(n => n.key));
  render();
}

function pickRow(label, items, selectedId, placeholder, icon, onSelect) {
  const sec = document.createElement('div');
  sec.className = 'cv-pick-row';
  sec.innerHTML = `<div class="cv-pick-label">${label}</div>`;
  sec.appendChild(buildDropdown({
    items, selectedId, placeholder, searchPlaceholder: placeholder, triggerIcon: icon, onSelect,
  }));
  return sec;
}

function renderPick() {
  const pick = root.querySelector('#dlPick');
  pick.innerHTML = '';

  pick.appendChild(pickRow('타이틀',
    titles.map(t => ({ id: t.id, name: t.name, meta: `${t.creatives.length} 대분류`, icon: TITLE_ICON })),
    dl.titleId, '타이틀 선택', TITLE_ICON,
    (item) => { dl = { titleId: item.id, creativeId: null, fileId: null }; selectedCanvasIds = new Set(); renderPick(); renderCanvases(); renderResults([]); updateHint(); }));

  const t = curTitle();
  if (t) {
    pick.appendChild(pickRow('대분류',
      t.creatives.map(c => ({ id: c.id, name: c.name, meta: `${(c.files || []).length} 소분류`, icon: CREATIVE_ICON })),
      dl.creativeId, '대분류 선택', CREATIVE_ICON,
      (item) => { dl.creativeId = item.id; dl.fileId = null; selectedCanvasIds = new Set(); renderPick(); renderCanvases(); renderResults([]); updateHint(); }));
  }

  const c = curCreative();
  if (c) {
    pick.appendChild(pickRow('소분류(파일)',
      (c.files || []).map(f => ({ id: f.id, name: f.name, meta: `${(f.canvases || []).length}개 캔버스`, icon: FILE_ICON })),
      dl.fileId, '파일 선택', FILE_ICON,
      (item) => {
        dl.fileId = item.id;
        const f = curFile();
        selectedCanvasIds = new Set((f && f.canvases ? f.canvases : []).map(cv => cv.id));
        renderPick(); renderCanvases(); renderResults([]); updateHint();
      }));
  }
}

function renderCanvases() {
  const sec = root.querySelector('#dlCanvasSec');
  if (!sec) return;
  const f = curFile();
  if (!f) { sec.innerHTML = ''; return; }
  const canvases = f.canvases || [];
  if (canvases.length === 0) {
    sec.innerHTML = `<div class="cv-section"><div class="cv-note">이 파일에는 캔버스가 없습니다.</div></div>`;
    return;
  }
  const allOn = canvases.every(cv => selectedCanvasIds.has(cv.id));
  sec.innerHTML = `
    <div class="cv-section">
      <div class="cv-section-label dl-canvas-head">
        <span>캔버스 (${canvases.length})</span>
        <button class="cv-link" id="dlToggleAll">${allOn ? '전체 해제' : '전체 선택'}</button>
      </div>
      <div class="cv-filelist" id="dlCanvasList">
        ${canvases.map(cv => {
          const vars = (cv.tw ? cv.tw.length : 0) + (cv.pkg ? Object.keys(cv.pkg).length : 0);
          return `<label class="cv-chip dl-canvas">
            <input type="checkbox" data-id="${cv.id}" ${selectedCanvasIds.has(cv.id) ? 'checked' : ''}>
            <span class="cv-chip-name">${escapeHtml(cv.name)}</span>
            <span class="cv-chip-meta">${vars}개 변수</span>
          </label>`;
        }).join('')}
      </div>
    </div>`;
  sec.querySelectorAll('#dlCanvasList input[type=checkbox]').forEach(inp => {
    inp.addEventListener('change', () => {
      if (inp.checked) selectedCanvasIds.add(inp.dataset.id); else selectedCanvasIds.delete(inp.dataset.id);
      updateHint();
    });
  });
  sec.querySelector('#dlToggleAll').addEventListener('click', (e) => {
    e.preventDefault();
    if (allOn) selectedCanvasIds.clear();
    else canvases.forEach(cv => selectedCanvasIds.add(cv.id));
    renderCanvases(); updateHint();
  });
}

function updateHint() {
  const hint = root.querySelector('#dlHint');
  const btn = root.querySelector('#dlBtn');
  if (!hint) return;
  // Any selection change invalidates a prior conversion → back to "변환".
  setMainBtnMode('convert');
  const f = curFile();
  const nCanvas = f ? selectedCanvasIds.size : 0;
  const nNet = selectedNetworks.size;
  if (!f) { hint.textContent = '소분류(파일)를 선택하세요'; if (btn) btn.disabled = true; return; }
  if (nCanvas === 0) { hint.textContent = '캔버스를 1개 이상 선택하세요'; if (btn) btn.disabled = true; return; }
  if (nNet === 0) { hint.textContent = '네트워크를 1개 이상 선택하세요'; if (btn) btn.disabled = true; return; }
  hint.textContent = `${nCanvas}개 캔버스 × ${nNet}개 네트워크 = ${nCanvas * nNet}개 변환`;
  if (btn) btn.disabled = false;
}

async function runDownload() {
  const btn = root.querySelector('#dlBtn');
  btn.disabled = true;
  const f = curFile();
  const canvases = (f ? f.canvases || [] : []).filter(cv => selectedCanvasIds.has(cv.id));
  const nets = NETWORKS.filter(n => selectedNetworks.has(n.key));
  const results = [];

  let source;
  try {
    source = await fetchFileSource(f.id);   // file's shared base, fetched once
  } catch (err) {
    for (const cv of canvases) for (const net of nets) results.push({ ok: false, inputName: cv.name, network: net, error: '원본 로드 실패: ' + err.message });
    renderResults(results); btn.disabled = false; updateHint(); return;
  }

  for (const cv of canvases) {
    for (const net of nets) {
      try {
        const { content, log } = buildForNetwork(source, cv, net.key);
        const bytes = utf8WithBom(content);
        const base = `${f.name}_${cv.name}`;
        const htmlName = outName(base, net, 'html');
        const zName = outName(base, net, 'zip');
        const htmlBlob = new Blob([bytes], { type: 'text/html' });
        const zipBlob = await makeZip([{ name: 'index.html', data: bytes }]);
        results.push({ ok: true, inputName: cv.name, network: net, htmlName, zipName: zName, htmlBlob, zipBlob, size: bytes.length, log });
      } catch (err) {
        results.push({ ok: false, inputName: cv.name, network: net, error: err.message });
      }
    }
  }
  renderResults(results);
  lastOkResults = results.filter(r => r.ok);
  updateHint();                                   // re-enables per selection, resets mode
  if (lastOkResults.length) setMainBtnMode('download');
  const mb = root.querySelector('#dlBtn');
  if (mb) mb.disabled = false;
}

function renderResults(results) {
  const el = root.querySelector('#dlResults');
  if (!el) return;
  if (!results.length) { el.innerHTML = ''; return; }
  const okOnes = results.filter(r => r.ok);
  el.innerHTML = `
    <div class="cv-results-head">
      <span>결과 · ${results.length}개 (성공 ${okOnes.length} / 실패 ${results.length - okOnes.length})</span>
    </div>
    <div class="cv-result-cards">${results.map((r, idx) => renderCard(r, idx)).join('')}</div>`;
  wireResultCards(el, results);
}

document.querySelector('#homeTabs a[data-home-tab="download"]')?.addEventListener('click', render);
